import { apiFetch } from './fetch';

export type CreditBalanceResponse = {
  userId: string;
  userEmail?: string | null;
  balance: number;
  reservedTotal: number;
  pendingShareCount: number;
  updatedAt: string;
};

export type CreditTransactionResponse = {
  id: string;
  amount: number;
  type: string;
  status: string;
  description?: string;
  referenceId?: string | null;
  createdAt: string;
};

export type CreditHistoryResponse = {
  balance: CreditBalanceResponse;
  transactions: CreditTransactionResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CreditHistoryFilters = {
  page?: number;
  pageSize?: number;
  type?: string;
  status?: string;
};

export async function fetchCreditBalance(): Promise<CreditBalanceResponse | null> {
  const res = await apiFetch('/api/billing/credits');
  if (!res.ok) return null;
  return (await res.json()) as CreditBalanceResponse;
}

export async function fetchCreditHistory(
  filters: CreditHistoryFilters = {},
): Promise<CreditHistoryResponse | null> {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);

  const query = params.toString();
  const res = await apiFetch(`/api/billing/credits/history${query ? `?${query}` : ''}`);
  if (!res.ok) return null;
  return (await res.json()) as CreditHistoryResponse;
}

export type UserCreditPackageDto = {
  id: string;
  name: string;
  credits: number;
  priceBRL: number;
  priceUSD: number;
  active: boolean;
  displayOrder: number;
  /** App Store consumable product id for this package (iOS IAP). */
  appleProductId?: string | null;
};

export type PaymentMethodDto = {
  id: string;
  provider: string;
  displayName: string;
  description: string | null;
  iconUrl: string | null;
  isEnabled: boolean;
  supportsSubscription: boolean;
  supportsOneTime: boolean;
  displayOrder: number;
};

export async function fetchUserCreditPackages(): Promise<UserCreditPackageDto[]> {
  const res = await apiFetch('/api/billing/credit-packages');
  if (!res.ok) {
    throw new Error(`Failed to fetch credit packages (${res.status})`);
  }
  return (await res.json()) as UserCreditPackageDto[];
}

export async function fetchUserPaymentMethods(): Promise<PaymentMethodDto[]> {
  const res = await apiFetch('/api/billing/payment-methods');
  if (!res.ok) return [];
  return (await res.json()) as PaymentMethodDto[];
}
